import { useEffect, useMemo, useRef, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import "animate.css";
import TrackVisibility from "react-on-screen";

const imageBase = "https://wiki.leagueoflegends.com/en-us/Special:Redirect/file";

const arcaneFrames = [
  {
    title: "Piltover Skyline",
    region: "City of Progress",
    description: "A clean visual direction for portfolio sections that need structure, premium spacing, and a strong first impression.",
    imgUrl: `${imageBase}/Piltover_Arcane_Hexgates.jpg`,
  },
  {
    title: "Jinx Concept",
    region: "Zaun Voltage",
    description: "The more experimental side: bold color, kinetic layouts, and details that keep the page from feeling generic.",
    imgUrl: `${imageBase}/Jinx_Arcane_Splash_Concept_01.jpg`,
  },
  {
    title: "Arcane Ensemble",
    region: "Piltover x Zaun",
    description: "A cinematic reference point for combining personal branding, project storytelling, and interactive sections.",
    imgUrl: `${imageBase}/Caitlyn_Jayce_Jinx_Vi_Viktor_Arcane_Promo_01.jpg`,
  },
];

const glyphs = [
  { id: "hex", icon: "HX", label: "Hex" },
  { id: "spark", icon: "SP", label: "Spark" },
  { id: "smoke", icon: "SM", label: "Smoke" },
  { id: "pulse", icon: "PL", label: "Pulse" },
];

const randomGlyph = () => glyphs[Math.floor(Math.random() * glyphs.length)];
const randomCell = (currentCell) => {
  const nextCell = Math.floor(Math.random() * 9);
  return nextCell === currentCell ? (nextCell + 1) % 9 : nextCell;
};

export const ArcaneLab = () => {
  const [sequence, setSequence] = useState([]);
  const [revealed, setRevealed] = useState(true);
  const [progress, setProgress] = useState([]);
  const [round, setRound] = useState(0);
  const [status, setStatus] = useState("Prime the console to begin.");
  const [activeGlyph, setActiveGlyph] = useState("");
  const cursorRef = useRef(null);
  const cursorFrame = useRef(null);
  const cursorPosition = useRef({ x: -120, y: -120 });
  const [huntActive, setHuntActive] = useState(false);
  const [huntCell, setHuntCell] = useState(4);
  const [huntScore, setHuntScore] = useState(0);
  const [huntTime, setHuntTime] = useState(20);
  const [runes, setRunes] = useState({
    violet: false,
    gold: false,
    teal: false,
  });

  const score = useMemo(() => Math.max(round - 1, 0), [round]);
  const runeCharge = Object.values(runes).filter(Boolean).length;
  const runeStatus = runeCharge === 3 ? "Core stabilized. The gate is open." : "Tune all three channels to stabilize the core.";

  useEffect(() => {
    const handleMouseMove = (event) => {
      cursorPosition.current = { x: event.clientX, y: event.clientY };

      if (cursorFrame.current) {
        return;
      }

      cursorFrame.current = window.requestAnimationFrame(() => {
        if (cursorRef.current) {
          cursorRef.current.style.transform = `translate3d(${cursorPosition.current.x}px, ${cursorPosition.current.y}px, 0)`;
        }

        cursorFrame.current = null;
      });
    };

    window.addEventListener("mousemove", handleMouseMove);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);

      if (cursorFrame.current) {
        window.cancelAnimationFrame(cursorFrame.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!huntActive) {
      return undefined;
    }

    const countdown = window.setInterval(() => {
      setHuntTime((time) => {
        if (time <= 1) {
          setHuntActive(false);
          return 0;
        }

        return time - 1;
      });
    }, 1000);

    return () => window.clearInterval(countdown);
  }, [huntActive]);

  const beginRound = (nextSequence, nextRound) => {
    setSequence(nextSequence);
    setProgress([]);
    setRound(nextRound);
    setRevealed(true);
    setStatus(`Round ${nextRound}: memorize the glyph chain.`);

    window.setTimeout(() => {
      setRevealed(false);
      setStatus(`Round ${nextRound}: replay the cipher from memory.`);
    }, 2200);
  };

  const startGame = () => {
    beginRound(Array.from({ length: 3 }, randomGlyph), 1);
  };

  const startHunt = () => {
    setHuntActive(true);
    setHuntScore(0);
    setHuntTime(20);
    setHuntCell(randomCell(huntCell));
  };

  const hitFirelight = (index) => {
    if (!huntActive || index !== huntCell) {
      return;
    }

    setHuntScore((currentScore) => currentScore + 1);
    setHuntCell((currentCell) => randomCell(currentCell));
  };

  const toggleRune = (rune) => {
    setRunes((currentRunes) => ({
      ...currentRunes,
      [rune]: !currentRunes[rune],
    }));
  };

  const handleGlyphPress = (glyphId) => {
    if (!sequence.length || revealed) {
      return;
    }

    setActiveGlyph(glyphId);
    window.setTimeout(() => setActiveGlyph(""), 180);

    const nextIndex = progress.length;
    const expected = sequence[nextIndex]?.id;

    if (glyphId !== expected) {
      setStatus("Pattern missed. Resetting the interaction.");
      setSequence([]);
      setProgress([]);
      setRound(0);
      setRevealed(true);
      return;
    }

    const nextProgress = [...progress, glyphId];
    setProgress(nextProgress);

    if (nextProgress.length === sequence.length) {
      const nextRound = round + 1;
      const extended = [...sequence, randomGlyph()];
      setStatus("Sync complete. Charging the next pattern...");
      window.setTimeout(() => beginRound(extended, nextRound), 900);
    }
  };

  return (
    <section className="arcane-lab" id="arcane-lab">
      <div
        ref={cursorRef}
        className="hextech-cursor"
      >
        <span></span>
        <i></i>
      </div>
      <Container>
        <TrackVisibility partialVisibility>
          {({ isVisible }) => (
            <div className={isVisible ? "animate__animated animate__fadeIn" : ""}>
              <div className="arcane-header">
                <span className="arcane-kicker">Arcane Mode</span>
                <h2>A portfolio layer with cinematic UI, motion, and small interactive moments.</h2>
                <p>
                  This section keeps the Arcane-inspired atmosphere, but the content still points back to the portfolio:
                  visual direction, frontend polish, and playful interactions that show personality without replacing the work.
                </p>
              </div>

              <Row className="g-4 align-items-stretch">
                <Col lg={7}>
                  <div className="arcane-gallery">
                    {arcaneFrames.map((frame) => (
                      <article className="arcane-card" key={frame.title}>
                        <img src={frame.imgUrl} alt={frame.title} />
                        <div className="arcane-card-copy">
                          <span>{frame.region}</span>
                          <h3>{frame.title}</h3>
                          <p>{frame.description}</p>
                        </div>
                      </article>
                    ))}
                  </div>
                </Col>

                <Col lg={5}>
                  <div className="hextech-console">
                    <span className="eyebrow">Mini game</span>
                    <h3>Hextech Cipher</h3>
                    <p>
                      A small interaction test that adds energy to the page while showing attention to state, feedback, and UI timing.
                    </p>

                    <div className="console-stats">
                      <div>
                        <strong>{score}</strong>
                        <span>Cleared rounds</span>
                      </div>
                      <div>
                        <strong>{sequence.length || 3}</strong>
                        <span>Glyph length</span>
                      </div>
                    </div>

                    <div className="cipher-sequence">
                      {sequence.length ? (
                        sequence.map((glyph, index) => (
                          <div className={`cipher-chip${revealed ? " is-visible" : ""}`} key={`${glyph.id}-${index}`}>
                            <span>{revealed ? glyph.icon : "?"}</span>
                            <small>{revealed ? glyph.label : "Hidden"}</small>
                          </div>
                        ))
                      ) : (
                        <div className="cipher-empty">Press ignite to generate the first pattern.</div>
                      )}
                    </div>

                    <div className="glyph-grid">
                      {glyphs.map((glyph) => (
                        <button
                          type="button"
                          key={glyph.id}
                          className={`glyph-pad${activeGlyph === glyph.id ? " is-active" : ""}`}
                          onClick={() => handleGlyphPress(glyph.id)}
                        >
                          <span>{glyph.icon}</span>
                          <small>{glyph.label}</small>
                        </button>
                      ))}
                    </div>

                    <div className="console-footer">
                      <p>{status}</p>
                      <button type="button" className="console-trigger" onClick={startGame}>
                        Start Cipher
                      </button>
                    </div>
                  </div>
                </Col>
              </Row>

              <div className="arcane-activities">
                <Row className="g-4">
                  <Col md={6}>
                    <div className="firelight-hunt">
                      <div className="activity-heading">
                        <span className="eyebrow">Reaction game</span>
                        <h3>Firelight Hunt</h3>
                        <p>A quick reaction game used as a playful break between project sections.</p>
                      </div>
                      <div className="hunt-stats">
                        <strong>{huntScore}</strong>
                        <span>{huntTime}s</span>
                      </div>
                      <div className="hunt-grid">
                        {Array.from({ length: 9 }).map((_, index) => (
                          <button
                            type="button"
                            className={`hunt-cell${huntActive && index === huntCell ? " is-live" : ""}`}
                            key={index}
                            onClick={() => hitFirelight(index)}
                          >
                            <span>{huntActive && index === huntCell ? "FL" : ""}</span>
                          </button>
                        ))}
                      </div>
                      <button type="button" className="console-trigger secondary-trigger" onClick={startHunt}>
                        Start Hunt
                      </button>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="rune-forge">
                      <div className="activity-heading">
                        <span className="eyebrow">Control panel</span>
                        <h3>Rune Forge</h3>
                        <p>A tiny control panel that mirrors how the portfolio balances color, motion, and interaction.</p>
                      </div>
                      <div className="rune-core">
                        <div className={`core-orb charge-${runeCharge}`}>
                          <span>{runeCharge}/3</span>
                        </div>
                        <p>{runeStatus}</p>
                      </div>
                      <div className="rune-switches">
                        {Object.keys(runes).map((rune) => (
                          <button
                            type="button"
                            className={`rune-switch ${rune}${runes[rune] ? " is-on" : ""}`}
                            key={rune}
                            onClick={() => toggleRune(rune)}
                          >
                            <span>{rune}</span>
                            <strong>{runes[rune] ? "online" : "offline"}</strong>
                          </button>
                        ))}
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            </div>
          )}
        </TrackVisibility>
      </Container>
    </section>
  );
};
